import type React from 'react';
import { useCallback, useRef } from 'react';
import styles from './tabs.module.scss';

export interface WordsHappenTabListProps {
  children?: React.ReactNode;
  /** The tab list's accessible name, e.g. "Account". */
  'aria-label'?: string;
  className?: string;
}

const NEXT_KEYS: Record<string, (index: number, count: number) => number> = {
  ArrowRight: (index, count) => (index + 1) % count,
  ArrowLeft: (index, count) => (index - 1 + count) % count,
  Home: () => 0,
  End: (_index, count) => count - 1,
};

/**
 * The row of tabs. The arrow keys, Home and End move focus between tabs; Enter or Space selects the focused one, so
 * moving through the tabs never changes the page by itself.
 */
export const WordsHappenTabList: React.FC<WordsHappenTabListProps> = ({
  children,
  'aria-label': ariaLabel,
  className,
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const next = NEXT_KEYS[event.key];
    const tabs = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[role="tab"]') ?? []);
    const index = tabs.indexOf(document.activeElement as HTMLElement);
    if (!next || index === -1) {
      return;
    }

    event.preventDefault();
    tabs[next(index, tabs.length)]?.focus();
  }, []);

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-orientation="horizontal"
      aria-label={ariaLabel}
      className={[styles.tabList, className].filter(Boolean).join(' ')}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
};
